import { Link } from "@/components/link";
import { PageCard, PageCardHeader, PageCardTitle } from "@/components/PageCard";
import PageHeader from "@/components/PageHeader";
import { CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Module, PATH_ROOT, PATH_SURVEYS } from "@/constants";
import usePaginatedList from "@/hooks/usePaginatedList";
import { fetchSurveyList } from "@/services/survey";
import { ReadSurvey } from "@/types/survey";
import { useInfiniteQuery } from "@tanstack/react-query";
import { CirclePlus, NotepadText } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const SurveyList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const querySurveys = async ({
    pageParam,
  }: {
    pageParam: string | undefined;
  }) => {
    return await fetchSurveyList({
      lastItem: pageParam,
      filters: [],
      sorting: undefined,
    });
  };

  const query = useInfiniteQuery({
    queryKey: [Module.Surveys],
    initialPageParam: undefined,
    queryFn: querySurveys,
    getNextPageParam: (lastPage) => {
      return lastPage.last_item;
    },
  });

  const editSurvey = (surveyId: string | undefined) => {
    navigate(`${PATH_SURVEYS}/${surveyId}`);
  };

  const surveys = usePaginatedList<ReadSurvey>(query);

  const breadcrumbItems = useMemo(
    () => [
      { label: t("breadcrumb.home"), href: PATH_ROOT },
      { label: t("breadcrumb.surveys") },
    ],
    [t],
  );

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <PageCard>
        <PageCardHeader>
          <PageCardTitle>
            <NotepadText className="mr-2" /> {t("surveyList.title")}
          </PageCardTitle>
          <Link to={`${PATH_SURVEYS}/new`}>
            <CirclePlus className="w-5 h-5 mr-3" />
            {t("surveyList.addSurvey")}
          </Link>
        </PageCardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/2">
                  {t("surveyList.questionnaireName")}
                </TableHead>
                <TableHead>
                  {t("surveyList.questionnaireDescription")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {surveys.map(({ id, name, description }) => (
                <TableRow
                  key={id}
                  className="cursor-pointer"
                  onClick={() => editSurvey(id)}
                >
                  <TableCell className="font-bold">{name}</TableCell>
                  <TableCell>{description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </PageCard>
    </>
  );
};

export default SurveyList;
